import React, { useState, useEffect } from "react";
import useStyles from "./style";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Slide,
  Typography,
  Button,
  IconButton,
  TextField,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useFormik } from "formik";
import { useDropzone } from "react-dropzone";
import * as yup from "yup";

// Libs
import { showLoader, hideLoader } from "Redux/Loader/actions";
import { showSnackbar } from "Redux/SnackBar/actions";
import { useDispatch, useSelector } from "react-redux";

// Services
import AppIconapi from "Services/AppIcon";
import ServiceFareApi from "Services/Servicefare";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AppIconCreate = (props) => {
  const { openDialog, handleClose } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const demoaccount_id = useSelector(
    (state) => state?.AuthReducer?.demoaccount_id || ""
  );
  const [iconImagePreview, setIconImagePreview] = useState(null);
  const [serviceList, setServiceList] = useState([]);

  useEffect(() => {
    if (openDialog) {
      ServiceFareApi.getServicesList({
        limit: "unlimited",
        ...(demoaccount_id ? { demoaccount_id } : {}),
      })
        .then((res) => {
          const list = res?.data?.data?.data || res?.data?.data || [];
          setServiceList(Array.isArray(list) ? list : []);
        })
        .catch((err) => {
          console.error("Failed to fetch service types", err);
          setServiceList([]);
        });
    }
  }, [openDialog, demoaccount_id]);

  const maxFileSize = 5242880;

  const {
    getRootProps: rootForIcon,
    getInputProps: inputForIcon,
    open: openForIcon,
  } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: maxFileSize,
    maxFiles: 1,
    accept: { "image/*": [] },
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          formik.setFieldValue("icon", file);
          setIconImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const handleAddIcon = (formValues) => {
    dispatch(showLoader("Loading please wait..."));

    const data = new FormData();
    data.append("name", formValues?.name?.trim());
    data.append("icon", formValues?.icon);
    data.append("status", formValues?.status);

    const stList = Array.isArray(formValues?.serviceTypes)
      ? formValues.serviceTypes
      : [];
    if (stList.length > 0) {
      stList.forEach((st) => {
        const id = st?._id || st;
        if (id && typeof id === "string") {
          data.append("serviceTypes", id);
        }
      });
    }

    if (demoaccount_id) {
      data.append("demoaccount_id", demoaccount_id);
    }

    if (formValues?.primaryservicetype) {
      data.append("primaryservicetype", formValues.primaryservicetype);
    }

    const onSuccess = (res) => {
      dispatch(hideLoader());
      if (res?.data?.status === true) {
        dispatch(
          showSnackbar({
            message: res?.data?.message || "App Icon Created Successfully",
            autoHideDuration: 3000,
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "success",
          })
        );

        formik.resetForm();
        setIconImagePreview(null);
        handleClose();
      }
    };

    const onFailure = (err) => {
      dispatch(hideLoader());
      dispatch(
        showSnackbar({
          message: err?.response?.data?.message || "Error while creating App Icon",
          autoHideDuration: 3000,
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "error",
        })
      );
    };

    AppIconapi.addAppIcon(data).then(onSuccess, onFailure);
  };

  const formik = useFormik({
    initialValues: {
      name: "",
      icon: null,
      serviceTypes: [],
      primaryservicetype: "",
      status: 1,
    },
    validationSchema: yup.object({
      name: yup.string().required("Icon Name is required"),
      icon: yup.mixed().required("Icon Image is required"),
    }),
    enableReinitialize: true,
    onSubmit: handleAddIcon,
  });

  const handleModalClose = () => {
    formik.resetForm();
    setIconImagePreview(null);
    handleClose();
  };

  return (
    <Dialog
      open={openDialog}
      TransitionComponent={Transition}
      keepMounted
      onClose={handleModalClose}
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle className={classes.headerBar}>
        <Typography className={classes.headerTitle}>Add App Icon</Typography>
        <IconButton aria-label="close" onClick={handleModalClose} className={classes.closeIconBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.contentContainer}>
        <Typography className={classes.sectionTitle}>Icon Image</Typography>

        <Box {...rootForIcon()} onClick={openForIcon} className={classes.uploadBox}>
          <input {...inputForIcon()} />
          {iconImagePreview ? (
            <Box
              component="img"
              src={iconImagePreview}
              alt="Icon preview"
              className={classes.previewImage}
            />
          ) : (
            <>
              <FileUploadOutlinedIcon className={classes.uploadIcon} />
              <Typography className={classes.uploadText}>
                Click to upload icon image
              </Typography>
            </>
          )}
        </Box>
        {formik.errors.icon && formik.touched.icon && (
          <Typography color="error" variant="caption" display="block" sx={{ mt: -1, mb: 2, textAlign: "center" }}>
            {formik.errors.icon}
          </Typography>
        )}

        <Typography className={classes.fieldLabel}>Icon Name</Typography>
        <TextField
          name="name"
          id="name"
          placeholder="Enter icon name"
          value={formik?.values?.name}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={Boolean(formik?.errors?.name && formik?.touched?.name)}
          helperText={
            formik?.errors?.name && formik?.touched?.name
              ? formik?.errors?.name
              : ""
          }
          fullWidth
          variant="outlined"
          className={classes.textField}
        />

        <Typography className={classes.fieldLabel}>Service Types</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            multiple
            displayEmpty
            name="serviceTypes"
            value={formik.values.serviceTypes || []}
            onChange={formik.handleChange}
            size="small"
            renderValue={(selected) => {
              if (!selected || selected.length === 0) {
                return (
                  <span style={{ color: "#9ca3af", fontSize: "14px" }}>
                    Select service types
                  </span>
                );
              }
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((val) => {
                    const matched = serviceList.find(
                      (s) => (s?._id || s) === (val?._id || val)
                    );
                    return (
                      <Chip
                        key={val?._id || val}
                        label={matched?.serviceName || val?.serviceName || val}
                        size="small"
                        sx={{
                          backgroundColor: "#f3f4f6",
                          color: "#1f2937",
                          fontSize: "12px",
                          fontWeight: 500,
                          borderRadius: "6px",
                          height: "24px",
                        }}
                      />
                    );
                  })}
                </Box>
              );
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 250,
                },
              },
            }}
          >
            {serviceList.length === 0 ? (
              <MenuItem disabled value="">
                <em>No service types available</em>
              </MenuItem>
            ) : (
              serviceList.map((service) => {
                const isChecked = (formik.values.serviceTypes || []).some(
                  (val) => (val?._id || val) === service._id
                );
                return (
                  <MenuItem key={service._id} value={service._id}>
                    <Checkbox checked={isChecked} size="small" sx={{ p: 0.5, mr: 1 }} />
                    <ListItemText
                      primary={service.serviceName}
                      primaryTypographyProps={{
                        fontSize: "14px",
                        fontFamily: "'Inter', sans-serif",
                      }}
                    />
                  </MenuItem>
                );
              })
            )}
          </Select>
        </FormControl>

        <Typography className={classes.fieldLabel}>Primary Service Type</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            displayEmpty
            name="primaryservicetype"
            value={formik.values.primaryservicetype || ""}
            onChange={formik.handleChange}
            size="small"
          >
            <MenuItem value="">
              <em>Select Primary Service Type</em>
            </MenuItem>
            {serviceList.map((service) => (
              <MenuItem key={service._id} value={service._id}>
                {service.serviceName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography className={classes.fieldLabel}>Status</Typography>
        <FormControl fullWidth className={classes.textField}>
          <Select
            name="status"
            value={formik.values.status}
            onChange={formik.handleChange}
            size="small"
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>

        <Box className={classes.buttonContainer}>
          <Button variant="outlined" onClick={handleModalClose} className={classes.cancelBtn}>
            Close
          </Button>
          <Button variant="contained" onClick={formik?.handleSubmit} className={classes.submitBtn}>
            Create Icon
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AppIconCreate;
